<template>
  <a-row>
    <!-- 搜索区域 -->
    <a-row style="margin-bottom: 16px;">
      <a-col span="4">
        <a-input
          v-model="queryForm.title"
          placeholder="专栏标题"
          allowClear
        />
      </a-col>
      <a-col span="16" style="padding-left: 8px; text-align: right;">
        <a-button type="primary" @click="handleSearch" :loading="loading">
          <a-icon type="search" /> 搜索
        </a-button>
        <a-button style="margin-left: 8px;" @click="handleReset">
          <a-icon type="reload" /> 重置
        </a-button>
        <CommonPermissions :p="$permissions.COLUMN.CREATE">
          <a-button type="primary" style="margin-left: 8px;" @click="handleCreate">
            <a-icon type="plus" /> 新建
          </a-button>
        </CommonPermissions>
      </a-col>
    </a-row>

    <!-- 数据表格 -->
    <a-table
      :columns="columns"
      :data-source="data"
      :pagination="{
        current: pagination.current,
        total: pagination.total,
        pageSize: pagination.pageSize,
        showSizeChanger: false,
        showTotal: (total) => `共 ${total} 条`
      }"
      :loading="loading"
      row-key="id"
      @change="handleTableChange"
    >
      <div slot="thumbUrl" slot-scope="text, record">
        <img
          v-if="record && record.thumbUrl"
          :src="record.thumbUrl"
          style="width: 80px; height: 60px; object-fit: cover; border-radius: 4px;"
          @error="handleImageError"
        />
        <div v-else style="width: 80px; height: 60px; background-color: #f5f5f5; border-radius: 4px; display: flex; align-items: center; justify-content: center; color: #999; font-size: 12px;">
          无封面
        </div>
      </div>

      <div slot="title" slot-scope="text, record">
        <div v-if="record" style="font-weight: bold; margin-bottom: 4px;">{{ record.title || '-' }}</div>
        <div v-if="record && record.summary" style="font-size: 12px; color: #666; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; max-width: 300px;">
          {{ record.summary }}
        </div>
      </div>

      <template slot="type" slot-scope="text, record">
        <template v-if="record">
          <span v-if="record.type === 1">数字课堂</span>
          <span v-else-if="record.type === 2">数字资源</span>
          <span v-else-if="record.type === 3">知识服务</span>
          <span v-else color="default">未知</span>
        </template>
        <span v-else>-</span>
      </template>

      <span slot="free" slot-scope="text, record">
        <template v-if="record">
          <a-tag v-if="record.free" color="green">免费</a-tag>
          <a-tag v-else color="blue">收费</a-tag>
        </template>
        <span v-else>-</span>
      </span>

      <span slot="price" slot-scope="text, record">
        {{ record && !record.free && record.price != null ? (record.price / 100).toFixed(2) + ' 元' : '-' }}
      </span>

      <span slot="status" slot-scope="text, record">
        <template v-if="record">
          <a-tag v-if="record.status === 0" color="orange">禁用</a-tag>
          <a-tag v-else-if="record.status === 1" color="green">启用</a-tag>
          <a-tag v-else color="default">未知</a-tag>
        </template>
        <span v-else>-</span>
      </span>

      <span slot="gmtCreate" slot-scope="text, record">
        {{ record && record.gmtCreate ? $moment(record.gmtCreate).format('YYYY-MM-DD HH:mm:ss') : '-' }}
      </span>

      <span slot="action" slot-scope="text, record">
        <template v-if="record">
          <CommonPermissions :p="$permissions.COLUMN.RETRIEVE">
            <a @click="() => viewColumn(record)">查看</a>
          </CommonPermissions>
          <CommonPermissions :p="$permissions.COLUMN.UPDATE">
            <a-divider type="vertical" />
            <a @click="() => editColumn(record, 'basic')">编辑信息</a>
          </CommonPermissions>
          <CommonPermissions :p="$permissions.COLUMN.UPDATE">
            <a-divider type="vertical" />
            <a @click="() => editColumn(record, 'content')">栏目内容</a>
          </CommonPermissions>
          <CommonPermissions :p="$permissions.COLUMN.UPDATE">
            <a-divider type="vertical" />
            <a
              v-if="record.status === 0"
              @click="() => toggleStatus(record)"
              style="color: #52c41a;"
            >
              启用
            </a>
            <a
              v-else-if="record.status === 1"
              @click="() => toggleStatus(record)"
              style="color: orange;"
            >
              禁用
            </a>
          </CommonPermissions>
          <CommonPermissions :p="$permissions.COLUMN.DELETE">
            <a-divider type="vertical" />
            <a-popconfirm
              title="确定要删除该专栏吗？"
              @confirm="() => deleteColumn(record)"
            >
              <a style="color: red;">删除</a>
            </a-popconfirm>
          </CommonPermissions>
        </template>
      </span>
    </a-table>

    <!-- 专栏详情抽屉 -->
    <a-drawer
      title="专栏详情"
      placement="right"
      width="60%"
      :closable="false"
      :visible="showDetailDrawer"
      @close="() => triggerDetailDrawer()"
    >
      <div v-if="currentColumn">
        <a-descriptions :column="1" bordered>
          <a-descriptions-item label="标题">{{ currentColumn.title || '-' }}</a-descriptions-item>
          <a-descriptions-item label="简介">{{ currentColumn.summary || '-' }}</a-descriptions-item>
          <a-descriptions-item label="类型">
            <a-tag v-if="currentColumn.type === 1" color="blue">数字课堂</a-tag>
            <a-tag v-else-if="currentColumn.type === 2" color="green">数字资源</a-tag>
            <a-tag v-else-if="currentColumn.type === 3" color="orange">知识服务</a-tag>
            <a-tag v-else color="default">未知</a-tag>
          </a-descriptions-item>
          <a-descriptions-item label="免费/收费">
            <a-tag v-if="currentColumn.free" color="green">免费</a-tag>
            <a-tag v-else color="orange">收费</a-tag>
          </a-descriptions-item>
          <a-descriptions-item label="价格" v-if="!currentColumn.free">
            {{ currentColumn.price != null ? (currentColumn.price / 100).toFixed(2) + ' 元' : '-' }}
          </a-descriptions-item>
          <a-descriptions-item label="状态">
            <a-tag v-if="currentColumn.status === 0" color="default">禁用</a-tag>
            <a-tag v-else-if="currentColumn.status === 1" color="green">启用</a-tag>
            <a-tag v-else color="default">未知</a-tag>
          </a-descriptions-item>
          <a-descriptions-item label="排序">{{ currentColumn.sortKey != null ? currentColumn.sortKey : '-' }}</a-descriptions-item>
          <a-descriptions-item label="封面图片" v-if="currentColumn.thumbUrl">
            <img
              :src="currentColumn.thumbUrl"
              style="max-width: 300px; max-height: 200px; border-radius: 4px;"
              @error="handleImageError"
            />
          </a-descriptions-item>
          <a-descriptions-item label="创建时间">
            {{ currentColumn.gmtCreate ? $moment(currentColumn.gmtCreate).format('YYYY-MM-DD HH:mm:ss') : '-' }}
          </a-descriptions-item>
          <a-descriptions-item label="修改时间">
            {{ currentColumn.gmtModified ? $moment(currentColumn.gmtModified).format('YYYY-MM-DD HH:mm:ss') : '-' }}
          </a-descriptions-item>
        </a-descriptions>

        <div style="margin-top: 24px; text-align: right;">
          <a-button @click="() => triggerDetailDrawer()">关闭</a-button>
        </div>
      </div>
    </a-drawer>

    <a-modal
      :title="editingId ? '编辑专栏' : '新增专栏'"
      :visible="editModalVisible"
      :confirm-loading="submitting"
      @ok="handleSubmitEdit"
      @cancel="handleCancelEdit"
    >
      <a-form-model
        ref="editFormRef"
        :model="editForm"
        :rules="editRules"
        :label-col="{ span: 6 }"
        :wrapper-col="{ span: 16 }"
      >
        <a-form-model-item label="标题" prop="title">
          <a-input v-model="editForm.title" placeholder="请输入专栏标题" allowClear />
        </a-form-model-item>
        <a-form-model-item label="类型">
          <span>{{ typeLabel(columnType) }}</span>
        </a-form-model-item>
        <a-form-model-item label="简介" prop="summary">
          <a-textarea v-model="editForm.summary" :rows="3" placeholder="请输入专栏简介" allowClear />
        </a-form-model-item>
        <a-form-model-item label="封面" prop="thumbUri">
          <Upload
            :defaultUrl="editForm.thumbUrl"
            @changeImgUri="handleThumbUriChange"
            path="img/column/cover/"
          />
        </a-form-model-item>
        <a-form-model-item label="是否免费" prop="free">
          <a-radio-group v-model="editForm.free">
            <a-radio :value="true">免费</a-radio>
            <a-radio :value="false">收费</a-radio>
          </a-radio-group>
        </a-form-model-item>
        <a-form-model-item v-if="!editForm.free" label="价格(元)" prop="priceYuan">
          <a-input-number
            v-model="editForm.priceYuan"
            :min="0"
            :precision="2"
            style="width: 180px;"
            placeholder="请输入价格"
          />
        </a-form-model-item>
        <a-form-model-item label="状态" prop="status">
          <a-radio-group v-model="editForm.status">
            <a-radio :value="1">启用</a-radio>
            <a-radio :value="0">禁用</a-radio>
          </a-radio-group>
        </a-form-model-item>
        <a-form-model-item label="排序" prop="sortKey">
          <a-input-number v-model="editForm.sortKey" :min="0" style="width: 180px;" />
        </a-form-model-item>
      </a-form-model>
    </a-modal>
  </a-row>
</template>

<script>
import ColumnApi from '@/api/src/column/api/ColumnApi'
import Upload from '@/components/Upload.vue'
import CommonPermissions from '@/components/CommonPermissions'

const columnApi = new ColumnApi()

const columns = [
  {
    title: '封面',
    dataIndex: 'thumbUrl',
    key: 'thumbUrl',
    scopedSlots: { customRender: 'thumbUrl' },
  },
  {
    title: '标题',
    key: 'title',
    scopedSlots: { customRender: 'title' },
  },
  {
    title: '免费/收费',
    dataIndex: 'free',
    key: 'free',
    scopedSlots: { customRender: 'free' },
  },
  {
    title: '价格',
    dataIndex: 'price',
    key: 'price',
    scopedSlots: { customRender: 'price' },
  },
  {
    title: '状态',
    dataIndex: 'status',
    key: 'status',
    scopedSlots: { customRender: 'status' },
  },
  {
    title: '创建时间',
    dataIndex: 'gmtCreate',
    key: 'gmtCreate',
    scopedSlots: { customRender: 'gmtCreate' },
  },
  {
    title: '操作',
    key: 'action',
    scopedSlots: { customRender: 'action' },
  },
]

const ROUTE_TYPE_MAP = {
  ColumnClassroom: 1,
  ColumnResource: 2,
  ColumnKnowledge: 3,
}

export default {
  name: 'ColumnList',
  components: { Upload, CommonPermissions },
  data() {
    return {
      columns,
      data: [],
      loading: false,
      showDetailDrawer: false,
      currentColumn: null,
      queryForm: {
        title: '',
      },
      pagination: {
        current: 1,
        total: 0,
        pageSize: 10,
      },
      editModalVisible: false,
      submitting: false,
      editingId: null,
      editForm: {
        title: '',
        summary: '',
        thumbUri: '',
        thumbUrl: '',
        free: true,
        priceYuan: 0,
        status: 1,
        sortKey: 0,
      },
      editRules: {
        title: [
          { required: true, message: '请输入专栏标题', trigger: 'blur' },
        ],
        free: [
          { required: true, message: '请选择是否免费', trigger: 'change' },
        ],
        status: [
          { required: true, message: '请选择状态', trigger: 'change' },
        ],
      },
    }
  },
  computed: {
    columnType() {
      return ROUTE_TYPE_MAP[this.$route.name] || 1
    },
  },
  watch: {
    '$route.name'() {
      this.queryForm.title = ''
      this.pagination.current = 1
      this.fetchList(1)
    },
  },
  async mounted() {
    await this.fetchList(1)
  },
  methods: {
    typeLabel(type) {
      if (type === 1) return '数字课堂'
      if (type === 2) return '数字资源'
      if (type === 3) return '知识服务'
      return '未知'
    },
    async fetchList(page) {
      this.loading = true
      try {
        const resp = await columnApi.findPagingColumn(page, {
          type: this.columnType,
          title: this.queryForm.title || undefined,
        })
        this.data = (resp && resp.list) ? resp.list : []
        this.pagination.total = (resp && resp.totalRow) ? resp.totalRow : 0
        this.pagination.current = (resp && resp.pageNumber) ? resp.pageNumber : page
        if (resp && resp.pageSize) {
          this.pagination.pageSize = resp.pageSize
        }
      } catch (error) {
        console.error('获取专栏列表失败:', error)
        this.$message.error('获取专栏列表失败')
      } finally {
        this.loading = false
      }
    },
    handleSearch() {
      this.pagination.current = 1
      this.fetchList(1)
    },
    handleReset() {
      this.queryForm = { title: '' }
      this.pagination.current = 1
      this.fetchList(1)
    },
    handleTableChange(pagination) {
      this.fetchList(pagination.current)
    },
    async viewColumn(record) {
      try {
        const resp = await columnApi.getColumnById(record.id)
        this.currentColumn = resp
        this.showDetailDrawer = true
      } catch (error) {
        console.error('获取专栏详情失败:', error)
        this.$message.error('获取专栏详情失败')
      }
    },
    editColumn(record, mode = 'basic') {
      if (mode === 'basic') {
        this.openEditModal(record)
        return
      }
      this.$router.push({
        name: 'ColumnEditor',
        query: { id: record.id, mode, type: this.columnType },
      })
    },
    handleCreate() {
      this.editingId = null
      this.editForm = {
        title: '',
        summary: '',
        thumbUri: '',
        thumbUrl: '',
        free: true,
        priceYuan: 0,
        status: 1,
        sortKey: 0,
      }
      this.editModalVisible = true
      this.$nextTick(() => {
        if (this.$refs.editFormRef) this.$refs.editFormRef.clearValidate()
      })
    },
    async openEditModal(record) {
      try {
        const detail = await columnApi.getColumnById(record.id)
        this.editingId = record.id
        this.editForm = {
          title: detail.title || '',
          summary: detail.summary || '',
          thumbUri: detail.thumbUri || '',
          thumbUrl: detail.thumbUrl || '',
          free: detail.free !== false,
          priceYuan: detail.price ? detail.price / 100 : 0,
          status: detail.status !== undefined ? detail.status : 1,
          sortKey: detail.sortKey || 0,
        }
        this.editModalVisible = true
        this.$nextTick(() => {
          if (this.$refs.editFormRef) this.$refs.editFormRef.clearValidate()
        })
      } catch (error) {
        console.error('获取专栏详情失败:', error)
        this.$message.error('获取专栏详情失败')
      }
    },
    async handleSubmitEdit() {
      this.$refs.editFormRef.validate(async (valid) => {
        if (!valid) return
        this.submitting = true
        try {
          const payload = {
            title: this.editForm.title,
            summary: this.editForm.summary || undefined,
            type: this.columnType,
            thumbUri: this.editForm.thumbUri || undefined,
            free: this.editForm.free,
            price: this.editForm.free ? 0 : Math.round((this.editForm.priceYuan || 0) * 100),
            status: this.editForm.status,
            sortKey: this.editForm.sortKey || 0,
          }

          if (this.editingId) {
            await columnApi.updateColumn(this.editingId, payload)
            this.$message.success('修改成功')
          } else {
            await columnApi.addColumn(payload)
            this.$message.success('新增成功')
          }
          this.editModalVisible = false
          await this.fetchList(this.pagination.current)
        } catch (error) {
          console.error('保存专栏失败:', error)
          this.$message.error('保存专栏失败')
        } finally {
          this.submitting = false
        }
      })
    },
    handleCancelEdit() {
      this.editModalVisible = false
      this.editingId = null
    },
    handleThumbUriChange(uri) {
      this.editForm.thumbUri = uri || ''
      if (!uri) {
        this.editForm.thumbUrl = ''
      }
    },
    async toggleStatus(record) {
      const newStatus = record.status === 1 ? 0 : 1
      try {
        await columnApi.updateColumnStatus(record.id, newStatus)
        this.$message.success(newStatus === 1 ? '已启用' : '已禁用')
        await this.fetchList(this.pagination.current)
      } catch (error) {
        console.error('修改状态失败:', error)
        this.$message.error('修改状态失败')
      }
    },
    async deleteColumn(record) {
      try {
        await columnApi.deleteColumn(record.id)
        this.$message.success('删除成功')
        if (this.currentColumn && this.currentColumn.id === record.id) {
          this.showDetailDrawer = false
          this.currentColumn = null
        }
        await this.fetchList(this.pagination.current)
      } catch (error) {
        console.error('删除专栏失败:', error)
        this.$message.error('删除专栏失败')
      }
    },
    triggerDetailDrawer() {
      this.showDetailDrawer = !this.showDetailDrawer
      if (!this.showDetailDrawer) {
        this.currentColumn = null
      }
    },
    handleImageError(event) {
      if (event.target) event.target.style.display = 'none'
    },
  },
}
</script>

<style scoped lang="less">
.ant-table-wrapper {
  margin-top: 16px;
}
</style>
